import {
  computed,
  readonly,
  ref,
  useContext,
} from '@nuxtjs/composition-api';

import { useApi } from '~/composables/useApi';
import { Logger } from '~/helpers/logger';
import { publicReadHeadersMatch, publicReadKey, publicReadScope, readPublicMetadata } from '~/helpers/publicReadCache';
import { useConfigStore } from '~/stores/config';

import { CurrencyGql } from './currency.gql';
import type {
  UseCurrencyChangeParams,
  UseCurrencyErrors,
  UseCurrencyInterface,
  UseCurrencyLoadParams,
} from './useCurrency';

/**
 * Allows loading and changing the currency.
 *
 * See the {@link UseCurrencyInterface} for a list of methods and values available in this composable.
 */
export function useCurrency(): UseCurrencyInterface {
  const context = useContext();
  const { app } = context;
  const pending = ref(0);
  const loading = computed(() => pending.value > 0);
  let loadSequence = 0;
  const error = ref<UseCurrencyErrors>({ load: null, change: null });
  const configStore = useConfigStore();
  const currency = computed(() => configStore.currency);
  const { query } = useApi();
  const scope = () => publicReadScope(app, context.$config?.currentSite);

  const load = async (params?: UseCurrencyLoadParams) => {
    error.value.load = null;
    pending.value += 1;
    const sequence = ++loadSequence;
    const requestedScope = scope();

    Logger.debug('useCurrency/load');

    try {
      const key = publicReadKey('currency', requestedScope, params ?? null);
      const loadedCurrency = await readPublicMetadata(app, key, async () => {
        const response = params?.customQuery || params?.customHeaders
          ? await app.$vsf.$magento.api.currency(params?.customQuery ?? null, params?.customHeaders ?? null)
          : await query<any>(CurrencyGql);
        const rawCurrency = response?.data?.currency;
        if (response?.errors?.length || !Array.isArray(rawCurrency?.available_currency_codes)) {
          throw new Error('Unable to load currency information');
        }
        return {
          ...rawCurrency,
          available_currency_codes: rawCurrency.available_currency_codes
            .map((item: any) => (typeof item === 'string' ? item : item?.code))
            .filter(Boolean),
        };
      });
      // A response for the previous Store/currency must not replace new context.
      if (requestedScope === scope() && sequence === loadSequence && publicReadHeadersMatch(app, params?.customHeaders)) {
        configStore.$patch((state) => {
          state.currency = loadedCurrency;
        });
      }
    } catch (err) {
      Logger.debug('[ERROR] useCurrency/load', err);
      if (requestedScope === scope() && sequence === loadSequence) error.value.load = err;
    } finally {
      pending.value -= 1;
    }
  };

  const change = (params: UseCurrencyChangeParams) => {
    error.value.change = null;
    Logger.debug('useCurrency/change', params);

    try {
      Logger.debug(params.id);
      app.$vsf.$magento.config.state.setCurrency(params.id);
      window.location.reload();
    } catch (err) {
      Logger.debug('[ERROR] useCurrency/change', err);
      error.value.change = err;
    }
  };

  return {
    load,
    change,
    currency,
    error: readonly(error),
    loading: readonly(loading),
  };
}

export * from './useCurrency';

export default useCurrency;
